/**
 * photoIntakeApi — the FE transport for the "Add from a photo" OCR
 * intake (photo_intake tool).
 *
 * Two pieces:
 *   1. downscaleImageForIntake — canvas-downscale a phone photo to a
 *      small JPEG and return its RAW base64 (no data: URI prefix). Phone
 *      photos are multi-MB; the server body cap is 1 MB and the vision
 *      model resizes anything over ~1568px on the long edge anyway, so we
 *      downscale to that BEFORE upload. Keeps the payload well under the
 *      cap and cuts model latency. Browser-only (needs canvas / Image);
 *      not exercised in jsdom CI.
 *   2. extractPhotoIntake — POST the base64 image to the streaming
 *      extract endpoint and return the structured rows. extractInsuranceCard
 *      does the same for an insurance card's front and back (kind
 *      'insurance_card'), after prepareCardImages fits the pair under
 *      the server's cap. Mirrors the
 *      NDJSON consume in api.ts's postNdjson (heartbeat lines ignored,
 *      terminal line acted on; in-band errors -> ApiError(502)).
 *
 * The image is EPHEMERAL: it is uploaded, read once by the model, and
 * never persisted anywhere (no client_documents blob). See the handler
 * header (api/src/functions/aiToolsPhotoIntake.ts) for the retention
 * rationale.
 *
 * The rows are never written here. Two hosts read them: the record's
 * "Add from a photo" confirms each row through the existing record-add
 * path (postRecordRow in clientRecordEditApi.ts); the Medication List tool
 * turns the medication rows into its list and writes nothing to the record.
 */

import { writeFetch } from './writeFetch';
import { ApiError } from './api';
import { PhotoUnreadableError } from './photoIntakeErrors';

/** One transcribed medication row (mirrors the record's med fields). */
/** An active medication on the record that a photo row matched. */
export interface PhotoRecordMedication {
  id: string;
  name: string;
  dose: string | null;
  schedule: string | null;
}

/** What a photo row is to the client's record, the server's word (LP-45,
 *  matchPhotoRowsToRecord): absent when the read had no client or the
 *  seat may not open the record. */
export type PhotoMedicationRecordMatch =
  | { state: 'on_record'; row: PhotoRecordMedication }
  | { state: 'near'; rows: PhotoRecordMedication[] }
  | { state: 'new' };

export type PhotoProviderRecordMatch = { state: 'on_record'; row: { id: string; display_name: string } } | { state: 'new' };

export interface PhotoIntakeMedicationRow {
  name: string;
  dose: string | null;
  schedule: string | null;
  prescriber: string | null;
  /** When it is taken, the record's ticks in the vocabulary's order (the
   *  photo reader's version 2, which always sends it); null or absent when
   *  the page gives no timing. */
  times_of_day?: string[] | null;
  record_match?: PhotoMedicationRecordMatch;
}

/** One transcribed provider row (mirrors the record's provider fields).
 *  specialty / organization are context-only — the record-add of a
 *  name-only provider does not carry them (they render from a linked
 *  Directory entry), so the confirm flow drops them. */
export interface PhotoIntakeProviderRow {
  display_name: string;
  role: string | null;
  specialty: string | null;
  organization: string | null;
  office_phone: string | null;
  /** The provider's email as the page or business card shows it (prompt
   *  v4, LP-32); null when none. */
  email: string | null;
  /** The five parts (prompt v3), or, from a model that answered in one
   *  line, the text alone. */
  office_address: { line1: string | null; line2: string | null; city: string | null; state: string | null; zip: string | null } | string | null;
  record_match?: PhotoProviderRecordMatch;
}

export interface PhotoIntakeResult {
  ai_invocation_id: number;
  medications: PhotoIntakeMedicationRow[];
  providers: PhotoIntakeProviderRow[];
}

export interface IntakeImage {
  media_type: 'image/jpeg';
  data: string;
}

/** Longest edge (px) the downscaled image is capped at — the vision
 *  model's own resize threshold, so anything larger is wasted bytes. */
const MAX_EDGE_PX = 1568;
const JPEG_QUALITY = 0.82;

export interface DownscaleOptions {
  /** Longest edge in px; the list reader's 1568 when absent. */
  maxEdge?: number;
  /** JPEG quality, 0 to 1; 0.82 when absent. */
  quality?: number;
}

/**
 * Downscale a selected image File to a small JPEG and return its raw
 * base64 + media type, ready for the extract body. Preserves aspect
 * ratio; never UPSCALES a small image. Browser-only. A file the browser
 * cannot decode, or no canvas to draw on, rejects with PhotoUnreadableError.
 */
export async function downscaleImageForIntake(file: File, opts: DownscaleOptions = {}): Promise<IntakeImage> {
  const bitmap = await loadImage(file);
  const { width, height } = fitWithin(bitmap.width, bitmap.height, opts.maxEdge ?? MAX_EDGE_PX);

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const cctx = canvas.getContext('2d');
  if (cctx === null) throw new PhotoUnreadableError('canvas 2d context unavailable');
  cctx.drawImage(bitmap, 0, 0, width, height);

  const dataUrl = canvas.toDataURL('image/jpeg', opts.quality ?? JPEG_QUALITY);
  const comma = dataUrl.indexOf(',');
  const data = comma >= 0 ? dataUrl.slice(comma + 1) : dataUrl;
  return { media_type: 'image/jpeg', data };
}

/** Scale (w,h) down so the longest edge is <= max; never scale up. */
export function fitWithin(w: number, h: number, max: number): { width: number; height: number } {
  const longest = Math.max(w, h);
  if (longest <= max) return { width: w, height: h };
  const scale = max / longest;
  return { width: Math.round(w * scale), height: Math.round(h * scale) };
}

function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new PhotoUnreadableError('could not read that image'));
    };
    img.src = url;
  });
}

/**
 * POST the base64 image to the streaming extract endpoint; return the
 * transcribed rows. Same NDJSON transport as the drafter generate calls:
 * pre-stream failures (403/429/400/413) come back as a non-2xx JSON body
 * -> ApiError(status, body); in-band terminal errors (post-200) ->
 * ApiError(502, { error: code }).
 */
export async function extractPhotoIntake(
  slug: string,
  args: { image: IntakeImage; clientId?: string },
  signal?: AbortSignal,
): Promise<PhotoIntakeResult> {
  return postExtract<PhotoIntakeResult>(
    slug,
    { image: args.image, ...(args.clientId ? { client_id: args.clientId } : {}) },
    signal,
  );
}

// ---------------------------------------------------------------------
// The insurance card read (kind 'insurance_card'): the "From a photo"
// door on Add insurance.
// ---------------------------------------------------------------------

/** One coverage program token (mirror of the record's COVERAGE_PROGRAMS). */
export type CardProgram =
  | 'medicare_a'
  | 'medicare_b'
  | 'medicare_a_b'
  | 'medicare_advantage'
  | 'part_d'
  | 'supplement'
  | 'medicaid'
  | 'ltc_insurance'
  | 'private'
  | 'other';

/** The card's text fields, named as the coverage form's boxes. */
export const CARD_TEXT_FIELDS = [
  'provider',
  'plan_name',
  'member_id',
  'group_number',
  'card_phone',
  'rx_bin',
  'rx_pcn',
  'rx_group',
  'holder_name',
  'holder_relationship',
] as const;

export type CardTextField = (typeof CARD_TEXT_FIELDS)[number];

/** What the reader read off a card: each box's value, or null. */
export type InsuranceCardRead = {
  program: CardProgram | null;
  /** The Medicare card shows Part B beside Part A. */
  also_part_b: boolean;
} & Record<CardTextField, string | null>;

export interface InsuranceCardResult {
  ai_invocation_id: number;
  card: InsuranceCardRead;
}

/** The server's cap on a card's images, together (base64 characters). */
export const CARD_IMAGES_MAX_BASE64 = 900 * 1024;
/** A card is small: 1280 px on the long edge keeps its print legible. */
const CARD_MAX_EDGE_PX = 1280;
/** The one lower quality tried when the pair is still over the cap. */
const CARD_RETRY_QUALITY = 0.6;

/**
 * The front and, when given, the back, downscaled for the card read. If
 * the two together are over the server's cap, both are re-encoded once at
 * a lower quality; still over, the read is refused here with the same
 * 413 the server gives, so the screen says the same sentence.
 */
export async function prepareCardImages(
  front: File,
  back?: File | null,
  downscale: (file: File, opts?: DownscaleOptions) => Promise<IntakeImage> = downscaleImageForIntake,
): Promise<IntakeImage[]> {
  const files = back ? [front, back] : [front];
  const total = (imgs: IntakeImage[]): number => imgs.reduce((n, i) => n + i.data.length, 0);
  let images = await Promise.all(files.map((f) => downscale(f, { maxEdge: CARD_MAX_EDGE_PX })));
  if (total(images) > CARD_IMAGES_MAX_BASE64) {
    images = await Promise.all(files.map((f) => downscale(f, { maxEdge: CARD_MAX_EDGE_PX, quality: CARD_RETRY_QUALITY })));
  }
  if (total(images) > CARD_IMAGES_MAX_BASE64) {
    throw new ApiError(
      413,
      { error: 'payload_too_large', limit_bytes: CARD_IMAGES_MAX_BASE64 },
      'insurance card photos over the cap after downscaling',
    );
  }
  return images;
}

/** POST the card's images (front first) and return what was read. */
export async function extractInsuranceCard(
  slug: string,
  args: { images: IntakeImage[]; clientId?: string },
  signal?: AbortSignal,
): Promise<InsuranceCardResult> {
  return postExtract<InsuranceCardResult>(
    slug,
    { kind: 'insurance_card', images: args.images, ...(args.clientId ? { client_id: args.clientId } : {}) },
    signal,
  );
}

/** A downscaled image as a file to keep in Documents (a JPEG, so `.jpg`). */
export function intakeImageFile(image: IntakeImage, name: string): File {
  const binary = atob(image.data);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return new File([bytes], name, { type: image.media_type });
}

/**
 * The shared NDJSON consume: pre-stream failures (403/429/400/413) come
 * back as a non-2xx JSON body -> ApiError(status, body); in-band terminal
 * errors (post-200) -> ApiError(502, { error: code }).
 */
async function postExtract<T>(slug: string, body: Record<string, unknown>, signal?: AbortSignal): Promise<T> {
  const path = `/api/t/${encodeURIComponent(slug)}/v1/ai-tools/photo-intake/extract`;
  const res = await writeFetch(path, {
    method: 'POST',
    headers: {
      Accept: 'application/x-ndjson, application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
    signal,
  });
  if (!res.ok) {
    let parsedBody: unknown;
    try {
      parsedBody = await res.json();
    } catch {
      parsedBody = await res.text().catch(() => null);
    }
    throw new ApiError(res.status, parsedBody, `POST ${path} → HTTP ${res.status}`);
  }
  const text = await res.text();
  let terminal: { type?: string; data?: unknown; error?: string } | null = null;
  for (const line of text.split('\n')) {
    const trimmed = line.trim();
    if (trimmed.length === 0) continue;
    let obj: { type?: string; data?: unknown; error?: string };
    try {
      obj = JSON.parse(trimmed) as typeof obj;
    } catch {
      continue;
    }
    if (obj.type === 'heartbeat') continue;
    if (obj.type === 'result' || obj.type === 'error') terminal = obj;
  }
  if (!terminal) {
    throw new ApiError(502, { error: 'stream_incomplete' }, `POST ${path} → incomplete NDJSON`);
  }
  if (terminal.type === 'error') {
    throw new ApiError(502, { error: terminal.error ?? 'anthropic_call_failed' }, `POST ${path} → ${terminal.error ?? 'error'}`);
  }
  return terminal.data as T;
}
